// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { buildMapCoordinatesKey, GetMapCoordinatesThunk } from './projectListThunk';
import { selectEffectiveOrderSortFilter } from './projectListSelector';

// Keeps the map's marker set in sync with the current search/filter: fetches the full coordinate
// list when map view opens and refetches when the keyword or filter changes while it is open.
// Without this the map would only plot the pages the grid's infinite scroll had loaded.
const useMapCoordinates = (showMapview: boolean) => {
  const dispatch = useAppDispatch();
  const mapCoordinatesLoading = useAppSelector((state) => state.projectList.mapCoordinatesLoading);
  const keywordFilter = useAppSelector((state) => state.projectList.keywordFilter);
  // The effective filter, so the refetch fires exactly when the value the thunk sends changes.
  const orderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);
  const mapCoordinatesKey = useAppSelector((state) => state.projectList.mapCoordinatesKey);
  // The key already dispatched this session, so back-to-back effect runs (map-view flip plus
  // a debounce flush landing in the same commit) send one request, not two.
  const requestedKeyRef = useRef('');

  useEffect(() => {
    if (!showMapview) return;
    const key = buildMapCoordinatesKey(keywordFilter, orderSortFilter);
    // The store already answers this exact keyword|filter - reopening map view rides the
    // cached set instead of re-running the unpaginated aggregation.
    if (key === mapCoordinatesKey) return;
    if (key === requestedKeyRef.current && mapCoordinatesLoading) return;
    requestedKeyRef.current = key;
    dispatch(GetMapCoordinatesThunk());
  }, [showMapview, keywordFilter, orderSortFilter]);
};

export default useMapCoordinates;
